import { Router } from 'express';
import { RateController } from '../controllers/rating.controller';
import { authMiddleware } from 'middlewares/authMiddleware';
import { permissionInjector } from 'middlewares/permissionInjector';
import { Routes } from 'interface/routes.interface';

/**
 * @swagger
 * tags:
 *   - name: Rating
 *     description: API endpoints for managing ratings
 */
export class RateRouter implements Routes {
    public router = Router();
    public rateController = new RateController();
    public path = '/rating';

    constructor() {
        this.initializeRoutes();
    }

    public initializeRoutes() {
        /**
         * @swagger
         * /rating/{userId}:
         *   post:
         *     summary: Create a new rating for a labourer
         *     tags: [Rating]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: userId
         *         required: true
         *         schema:
         *           type: string
         *         description: The ID of the labourer being rated
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               rating:
         *                 type: integer
         *                 minimum: 1
         *                 maximum: 5
         *                 description: The rating value (1-5)
         *               feedback:
         *                 type: string
         *                 maxLength: 500
         *                 description: Optional feedback for the rating
         *     responses:
         *       201:
         *         description: Rating created successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: "created"
         *                 message:
         *                   type: string
         *                   example: "Successfully created Rating.rating"
         *                 data:
         *                   $ref: '#/components/schemas/RatingResponse'
         *       400:
         *         description: Invalid input or self-rating attempt
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (e.g., not a co-worker)
         *       404:
         *         description: Labourer or rater not found
         *       500:
         *         description: Internal server error
         */
        this.router.post(
            `${this.path}/:userId`,
            authMiddleware,
            permissionInjector(`${this.path}`, 'POST'),
            this.rateController.createRate
        );

        /**
         * @swagger
         * /rating:
         *   get:
         *     summary: Get all ratings
         *     tags: [Rating]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           default: 1
         *         description: Page number
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           default: 10
         *         description: Number of items per page
         *       - in: query
         *         name: userId
         *         schema:
         *           type: string
         *         description: Filter by labourer ID (restricted to self for Labour role)
         *     responses:
         *       200:
         *         description: Successfully retrieved ratings
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: "ok"
         *                 message:
         *                   type: string
         *                   example: "Successfully fetched Rating.ratings"
         *                 data:
         *                   type: array
         *                   items:
         *                     $ref: '#/components/schemas/RatingResponse'
         *                 total:
         *                   type: integer
         *                   example: 10
         *                 page:
         *                   type: integer
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   example: 2
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (Labour role restricted)
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}`,
            authMiddleware,
            permissionInjector(`${this.path}`, 'GET'),
            this.rateController.getAllRatings
        );

        /**
         * @swagger
         * /rating/{id}:
         *   get:
         *     summary: Get a specific rating by ID
         *     tags: [Rating]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: The ID of the rating
         *     responses:
         *       200:
         *         description: Successfully retrieved rating
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: "ok"
         *                 message:
         *                   type: string
         *                   example: "Successfully fetched Rating.rating"
         *                 data:
         *                   $ref: '#/components/schemas/RatingResponse'
         *       400:
         *         description: Invalid ID
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (Labour role restricted to own ratings)
         *       404:
         *         description: Rating not found
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}/:id`,
            authMiddleware,
            permissionInjector(`${this.path}/:id`, 'GET'),
            this.rateController.getRatingById
        );

        /**
         * @swagger
         * /rating/{id}:
         *   put:
         *     summary: Update a rating by ID
         *     tags: [Rating]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: id
         *         required: true
         *         schema:
         *           type: string
         *         description: The ID of the rating to update
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               rating:
         *                 type: integer
         *                 minimum: 1
         *                 maximum: 5
         *                 description: The updated rating value (1-5)
         *               feedback:
         *                 type: string
         *                 maxLength: 500
         *                 description: The updated feedback
         *     responses:
         *       200:
         *         description: Rating updated successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: "ok"
         *                 message:
         *                   type: string
         *                   example: "Successfully updated Rating.rating"
         *                 data:
         *                   $ref: '#/components/schemas/RatingResponse'
         *       400:
         *         description: Invalid input
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (only Admin/Staff can update)
         *       404:
         *         description: Rating not found
         *       500:
         *         description: Internal server error
         */
        this.router.put(
            `${this.path}/:id`,
            authMiddleware,
            permissionInjector(`${this.path}/:id`, 'PUT'),
            this.rateController.updateRating
        );

        /**
         * @swagger
         * /rating:
         *   delete:
         *     summary: Delete multiple ratings by IDs
         *     tags: [Rating]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               ids:
         *                 type: array
         *                 items:
         *                   type: string
         *                 description: Array of rating IDs to delete
         *     responses:
         *       200:
         *         description: Ratings deleted successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: "ok"
         *                 message:
         *                   type: string
         *                   example: "Successfully deleted Rating.ratings"
         *                 data:
         *                   type: object
         *                   properties:
         *                     deleted:
         *                       type: integer
         *                       example: 2
         *       400:
         *         description: Invalid IDs
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (only Admin/Staff can delete)
         *       500:
         *         description: Internal server error
         */
        this.router.delete(
            `${this.path}`,
            authMiddleware,
            permissionInjector(`${this.path}`, 'DELETE'),
            this.rateController.deleteMultipleRatings
        );
        /**
         * @swagger
         * /rating/getByUserId/{userId}:
         *   get:
         *     summary: Get all ratings of User 
         *     tags: [Rating]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: userId
         *         required: true
         *         schema:
         *           type: string
         *         description: The ID of the rating
         *     responses:
         *       200:
         *         description: Successfully retrieved ratings of User
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: "ok"
         *                 message:
         *                   type: string
         *                   example: "Successfully fetched Rating"
         *                 data:
         *                   type: array
         *                   items:
         *                     $ref: '#/components/schemas/RatingResponse'
         *                 total:
         *                   type: integer
         *                   example: 10
         *                 page:
         *                   type: integer
         *                   example: 1
         *                 totalPage:
         *                   type: integer
         *                   example: 2
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (Labour role restricted)
         *       500:
         *         description: Internal server error
         */
        this.router.get(`${this.path}/getByUserId/:userId`, permissionInjector(`${this.path}`, 'GET'), this.rateController.getRatingByUserId)
    }
}

/**
 * @swagger
 * components:
 *   schemas:
 *     RatingResponse:
 *       type: object
 *       properties:
 *         _id:
 *           type: string
 *           description: The rating ID
 *         rating:
 *           type: integer
 *           minimum: 1
 *           maximum: 5
 *           description: The rating value
 *         feedback:
 *           type: string
 *           maxLength: 500
 *           nullable: true
 *           description: Feedback for the rating
 *         isDeleted:
 *           type: boolean
 *           description: Whether the rating is deleted
 *         deletedAt:
 *           type: string
 *           format: date-time
 *           nullable: true
 *           description: The deletion date of the rating
 *         createdAt:
 *           type: string
 *           format: date-time
 *           description: The creation date of the rating
 *         updatedAt:
 *           type: string
 *           format: date-time
 *           description: The last update date of the rating
 *         labour:
 *           type: object
 *           nullable: true
 *           properties:
 *             firstName:
 *               type: string
 *             lastName:
 *               type: string
 *             email:
 *               type: string
 *             profileImage:
 *               type: object
 *               nullable: true
 *               properties:
 *                 _id:
 *                   type: string
 *                 filename:
 *                   type: string
 *                 url:
 *                   type: string
 *         rater:
 *           type: object
 *           nullable: true
 *           properties:
 *             firstName:
 *               type: string
 *             lastName:
 *               type: string
 *             email:
 *               type: string
 *             profileImage:
 *               type: object
 *               nullable: true
 *               properties:
 *                 _id:
 *                   type: string
 *                 filename:
 *                   type: string
 *                 url:
 *                   type: string
 *       required:
 *         - _id
 *         - rating
 *         - isDeleted
 *         - createdAt
 *         - updatedAt
 *   securitySchemes:
 *     bearerAuth:
 *       type: http
 *       scheme: bearer
 *       bearerFormat: JWT
 */